import { Sound, Track, TrackOptions } from '@1x2network/howler_sound'
import gsap from 'gsap'
import { toSeconds } from './toSeconds'
import { CreateSoundOptions } from '@1x2network/howler_sound/lib/sound/createSound'

type BackgroundSound = Sound & { localVolume: number; maxVolume: number }

/**
 * Handles switching between background music tracks and fading them in and out
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class BackgroundMusic {
    private track: Track
    private currentTier = 0
    private sounds: BackgroundSound[] = []
    private _volume = 1
    private specification: BackgroundMusicSpecification

    public get volume(): number {
        return this._volume
    }

    public set volume(value: number) {
        this._volume = value
        this.sounds.forEach((sound) => {
            sound.active.volume(sound.localVolume * value)
        })
    }

    constructor(spec: BackgroundMusicSpecification) {
        this.specification = spec
        this.track = new Track(spec.track)
        this.volume = 1

        this.track.getSound().forEach((sound) => {
            sound.volume = 0
            let localVolume = 0
            Object.defineProperty(sound, 'localVolume', {
                get: () => localVolume,
                set: (v: number) => {
                    localVolume = v
                    sound.active.volume(v * this.volume)
                },
            })

            Object.defineProperty(sound, 'maxVolume', {
                value: (sound.options as BackgroundSoundTrackOptions).maxVolume ?? 1,
            })
        })

        this.sounds = this.track.getSound() as BackgroundSound[]
        this.sounds[0].localVolume = this.sounds[0].maxVolume
    }

    public fade(to: number, duration: number): void {
        gsap.to(this, { volume: to, duration, overwrite: true })
    }

    public setTier(tier: number): void {
        const { transition } = this.specification
        const currentSound = this.sounds[this.currentTier]
        const nextSound = this.sounds[tier]
        this.currentTier = tier
        gsap.killTweensOf([currentSound, nextSound])
        gsap.to(currentSound, { localVolume: 0, ease: 'none', duration: toSeconds(transition) })
        gsap.to(nextSound, { localVolume: nextSound.maxVolume, ease: 'none', duration: toSeconds(transition) })
    }
}

type BackgroundSoundTrackOptions = Omit<CreateSoundOptions, 'autoplay' | 'loop' | 'volume'> & {
    maxVolume?: number
}

export interface BackgroundMusicSpecification {
    track: Omit<TrackOptions, 'sounds'> & { sounds: Record<string, BackgroundSoundTrackOptions> }
    transition: number
}
